<template>
	<v-dialog :value="show" max-width="480" @click:outside="$emit('close')" @keydown.esc="$emit('close')">
		<v-card>
			<v-card-title class="d-flex align-center justify-space-between py-3 px-4">
				<span style="font-size:15px;font-weight:700;color:#2D4B95;">
					{{ isEditing ? 'Editar Columna' : 'Nueva Columna' }}
				</span>
				<v-btn icon small @click="$emit('close')">
					<v-icon small>mdi-close</v-icon>
				</v-btn>
			</v-card-title>
			<v-divider></v-divider>

			<v-card-text class="pt-4">
				<v-form ref="form">
					<v-text-field
						v-model="form.name"
						label="Nombre de la columna"
						outlined
						dense
						:rules="[v => !!v || 'El nombre es requerido']"
						autofocus
						prepend-inner-icon="mdi-view-column-outline"
					/>

					<div class="mb-2">
						<p class="caption mb-2" style="color:#2D4B95;font-weight:600;">Color de la columna</p>
						<div class="color-palette d-flex flex-wrap" style="gap:8px;">
							<div
								v-for="color in colorPalette"
								:key="color"
								class="color-swatch"
								:style="{ backgroundColor: color, outline: form.color === color ? '3px solid #2D4B95' : '2px solid transparent' }"
								@click="form.color = color"
							>
								<v-icon v-if="form.color === color" small color="white">mdi-check</v-icon>
							</div>
						</div>
						<div class="d-flex align-center mt-3" style="gap:8px;">
							<span class="caption">Color personalizado:</span>
							<input type="color" v-model="form.color" class="color-input" />
							<span class="caption" :style="{ color: form.color }">{{ form.color }}</span>
						</div>
					</div>
				</v-form>
			</v-card-text>

			<v-divider></v-divider>
			<v-card-actions class="px-4 py-3">
				<v-btn
					v-if="isEditing"
					text
					color="red"
					small
					:loading="loadingDelete"
					:disabled="loading"
					@click="handleDelete"
				>
					<v-icon small class="mr-1">mdi-delete-outline</v-icon>
					Eliminar
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn text small :disabled="loading || loadingDelete" @click="$emit('close')">Cancelar</v-btn>
				<v-btn
					color="#2D4B95"
					dark
					small
					:loading="loading"
					:disabled="loadingDelete"
					@click="handleSave"
				>
					{{ isEditing ? 'Guardar cambios' : 'Crear columna' }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { createProjectColumn, updateProjectColumn, deleteProjectColumn } from '@/helpers/api/kanban'

export default {
	name: 'KanbanColumnModal',
	props: {
		show: {
			type: Boolean,
			default: false
		},
		column: {
			type: Object,
			default: null
		},
		nextPosition: {
			type: Number,
			default: 1
		},
		projectId: {
			type: Number,
			required: true
		}
	},
	emits: ['close', 'saved', 'deleted'],
	data: () => ({
		loading: false,
		loadingDelete: false,
		form: {
			name: '',
			color: '#5e85ef'
		},
		colorPalette: [
			'#5e85ef', '#2D4B95', '#34BFA3', '#40C2A8',
			'#dcd614', '#fb8c00', '#F4516C', '#9C27B0',
			'#607D8B', '#795548', '#009688', '#E91E63'
		]
	}),
	computed: {
		isEditing() {
			return !!this.column
		}
	},
	watch: {
		show(val) {
			if (val) {
				this.loadForm()
			}
		}
	},
	methods: {
		loadForm() {
			if (this.column) {
				this.form.name = this.column.name || ''
				this.form.color = this.column.color || '#5e85ef'
			} else {
				this.form.name = ''
				this.form.color = '#5e85ef'
			}
			this.$nextTick(() => {
				this.$refs.form && this.$refs.form.resetValidation()
			})
		},
		async handleSave() {
			if (!this.$refs.form.validate()) return
			this.loading = true
			try {
				if (this.isEditing) {
					const resp = await updateProjectColumn({
						column_id: this.column.id,
						name: this.form.name,
						color: this.form.color,
						project_id: this.projectId
					})
					if (resp.code === 200) {
						this.$emit('saved', resp.column)
					}
				} else {
					const resp = await createProjectColumn({
						name: this.form.name,
						color: this.form.color,
						position: this.nextPosition,
						project_id: this.projectId
					})
					if (resp.code === 200) {
						this.$emit('saved', resp.column)
					}
				}
			} catch (e) {
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Error al guardar la columna. Por favor intenta nuevamente.',
					title: 'Error',
					created: new Date(),
					type: 'danger',
					icon: 'mdi-close-octagon',
					color: '#F4516C'
				})
			} finally {
				this.loading = false
			}
		},
		async handleDelete() {
			this.loadingDelete = true
			try {
				const resp = await deleteProjectColumn({ column_id: this.column.id })
				if (resp.code === 200) {
					this.$emit('deleted', this.column.id)
				}
			} catch (e) {
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Error al eliminar la columna.',
					title: 'Error',
					created: new Date(),
					type: 'danger',
					icon: 'mdi-close-octagon',
					color: '#F4516C'
				})
			} finally {
				this.loadingDelete = false
			}
		}
	}
}
</script>

<style scoped>
.color-swatch {
	width: 30px;
	height: 30px;
	border-radius: 50%;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: transform 0.1s ease;
}
.color-swatch:hover {
	transform: scale(1.15);
}
.color-input {
	width: 36px;
	height: 28px;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	padding: 0;
}
</style>
